import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, App, Space } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { buildClientRedirectUrl, resolveCurrentClientType } from '../../lib/clientType';
import { usePartnerAuthStore } from '../../stores/partnerAuthStore';
import { SERVICE_TITLE } from '@shared/config/service-config';

const { Title, Text } = Typography;

interface PartnerLoginForm {
  email: string;
  password: string;
}

type PartnerLoginSuccessResponse = {
  partnerUser: {
    id: string;
    email: string;
    name: string;
    role: string;
    partnerCompanyId: string;
    partnerCompanyName: string | null;
  };
  token: string;
  expiresAt: Date | string;
};

type PartnerLoginRedirectResponse = {
  redirectRequired: true;
  targetClientType: string;
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isPartnerLoginRedirectResponse = (value: unknown): value is PartnerLoginRedirectResponse => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return value.redirectRequired === true &&
    typeof value.targetClientType === 'string';
};

const isPartnerLoginSuccessResponse = (value: unknown): value is PartnerLoginSuccessResponse => {
  if (!isObjectRecord(value) || !isObjectRecord(value.partnerUser)) {
    return false;
  }

  return typeof value.token === 'string' &&
    (typeof value.expiresAt === 'string' || value.expiresAt instanceof Date) &&
    typeof value.partnerUser.id === 'string' &&
    typeof value.partnerUser.email === 'string' &&
    typeof value.partnerUser.name === 'string' &&
    typeof value.partnerUser.role === 'string' &&
    typeof value.partnerUser.partnerCompanyId === 'string';
};

export function PartnerLoginPage() {
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const { setAuth } = usePartnerAuthStore();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const loginMutation = trpc.partnerAuth.login.useMutation({
    onSuccess: (data) => {
      if (isPartnerLoginRedirectResponse(data)) {
        const redirectUrl = buildClientRedirectUrl(data.targetClientType);
        if (redirectUrl) {
          messageApi.info('正しいログイン画面へ移動します。');
          window.location.href = redirectUrl;
          return;
        }
        messageApi.error('ログイン先の判定に失敗しました');
        setLoading(false);
        return;
      }

      if (!isPartnerLoginSuccessResponse(data)) {
        messageApi.error('ログイン応答が不正です');
        setLoading(false);
        return;
      }

      setAuth(data.partnerUser, data.token, data.expiresAt);
      messageApi.success('ログインに成功しました');
      setLoading(false);
      navigate('/partner/dashboard', { replace: true });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error
        ? error.message
        : 'ログインに失敗しました';
      messageApi.error(errorMessage);
      setLoading(false);
    },
  });

  const handleSubmit = async (values: PartnerLoginForm) => {
    setLoading(true);
    loginMutation.mutate({
      ...values,
      clientType: resolveCurrentClientType(),
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2c3e50 0%, #4b79a1 100%)',
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ marginBottom: '8px' }}>
              パートナーポータル
            </Title>
            <Text type="secondary">{SERVICE_TITLE}</Text>
          </div>

          <Form
            form={form}
            name="partnerLogin"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'メールアドレスを入力してください' },
                { type: 'email', message: '有効なメールアドレスを入力してください' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="メールアドレス"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'パスワードを入力してください' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="パスワード"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                ログイン
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Space direction="vertical" size="small">
              <Link to="/">
                <Text type="secondary">トップページに戻る</Text>
              </Link>
              <Link to="/login">
                <Text type="secondary">通常ログインはこちら</Text>
              </Link>
            </Space>
          </div>
        </Space>
      </Card>
    </div>
  );
}
